/**
 * UserSmsSection — v2.5.135 SMS-A
 * ─────────────────────────────────────────────────────────────────
 * Lives in ProfilePage. Lets the user add a phone, verify it,
 * mark a primary, opt in/out, delete.
 *
 * Backend at /api/me/sms/*. The verification SMS goes out
 * automatically when a number is added.
 *
 * UI is deliberately conservative: one number per row, clear
 * verify/opt-in states, explicit explanation of why each step
 * is needed (we're capturing TCPA consent — being clumsy about
 * it gets us blocked by carriers).
 */

import { useEffect, useState } from 'react';
import { MessageSquare, Phone, CheckCircle2, AlertCircle, Star, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api.js';
// 2026-10-09 — the Messaging plan is a digital subscription sold through
// Stripe. The App Store and Google Play only allow that through their own
// in-app purchase, so the native apps show the plan's status but never a
// way to buy, change or pay for it (no buttons, no prices, no "go to the
// web" prompt). The web app is unchanged.
import { IS_NATIVE } from '../utils/apiBase.js';

export default function UserSmsSection() {
  const [phones, setPhones] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const refresh = async () => {
    try {
      const [phonesRes, subRes] = await Promise.all([
        api.get('/me/sms/phones'),
        api.get('/me/sms/subscription'),
      ]);
      setPhones(phonesRes.data.phones || []);
      setSubscription(subRes.data.subscription);
    } catch (err) {
      console.error('UserSmsSection load failed:', err);
      setPhones([]);
      setSubscription({ is_active: false });
    }
  };

  useEffect(() => { refresh(); }, []);

  // v2.5.136 — When the user returns from Stripe Checkout, the
  // URL has ?sms_subscribed=1 (success) or =cancelled (back-out).
  // Stripe's redirect can fire BEFORE the customer.subscription.created
  // webhook arrives, so we poll the subscription status for a few
  // seconds to give the backend time to sync. On success we toast
  // and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('sms_subscribed');
    if (!flag) return;

    // Clean the URL first so a refresh doesn't retrigger.
    const cleaned = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleaned);

    if (flag === 'cancelled') {
      toast('Subscription not started.', { icon: 'ℹ️' });
      return;
    }
    if (flag !== '1') return;

    // Success — webhook may take a few seconds. Poll every 1.5s up
    // to ~15s, refreshing the UI state until status flips to
    // trialing/active.
    //
    // v2.5.138 audit B26 — track an `isMounted` flag so if the user
    // navigates away from Profile mid-poll, we don't keep calling
    // refresh() (which calls setState) on an unmounted component.
    let isMounted = true;
    toast.success('Subscription started — confirming with our servers…');
    let attempts = 0;
    const tick = async () => {
      if (!isMounted) return;
      attempts++;
      try {
        const { data } = await api.get('/me/sms/subscription');
        if (!isMounted) return;
        const s = data.subscription;
        if (s && (s.status === 'trialing' || s.status === 'active')) {
          await refresh();
          if (isMounted) toast.success('You\'re all set!');
          return;
        }
      } catch { /* ignore */ }
      if (!isMounted) return;
      if (attempts < 10) setTimeout(tick, 1500);
      else {
        // Webhook took too long — refresh anyway so the user sees
        // whatever's there.
        refresh().catch(() => {});
      }
    };
    setTimeout(tick, 1500);
    return () => { isMounted = false; };
  }, []);

  if (phones === null) {
    return (
      <div className="card mb-5">
        <div className="text-secondary text-xs">Loading SMS settings…</div>
      </div>
    );
  }

  return (
    <div className="card mb-5">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare size={18} style={{ color: 'var(--ice)' }} />
        <h2 className="text-lg font-bold">SMS Notifications</h2>
      </div>
      <p className="text-secondary text-xs mb-4 -mt-1">
        Add and verify a phone number to receive team SMS notifications.
        SMS sends only when your subscription is active AND your team manager has SMS enabled.
      </p>

      {/* Subscription status badge */}
      <SubscriptionStatus subscription={subscription} />

      {/* Phone list */}
      {phones.length === 0 ? (
        <div className="text-secondary text-xs py-3" style={{ marginTop: 12 }}>
          No phone numbers yet. Add one below to start receiving SMS.
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {phones.map(p => (
            <PhoneRow key={p.id} phone={p} onChange={refresh} />
          ))}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <AddPhoneForm onAdded={refresh} hasAny={phones.length > 0} />
      </div>
    </div>
  );
}

function SubscriptionStatus({ subscription }) {
  const active = !!subscription?.is_active;
  const plan = subscription?.plan;
  const status = subscription?.status;
  const trialEnd = subscription?.trial_end;
  const periodEnd = subscription?.current_period_end;
  const cancelAtPeriodEnd = !!subscription?.cancel_at_period_end;
  const paymentFailed = !!subscription?.last_payment_failed_at;
  const hasStripeCustomer = !!subscription?.has_stripe_customer;

  const [busy, setBusy] = useState(false);

  // Stripe states we care about for UI:
  //   trialing — in 7-day trial; show trial end date
  //   active   — paid + current; show "renews on" or "cancels on"
  //   past_due — payment failed, dunning; show "update payment"
  //   canceled / unpaid / incomplete / null — show Subscribe options
  const inTrial = status === 'trialing';
  const isPaidActive = status === 'active';
  const isPastDue = status === 'past_due';
  const isGranted = plan === 'granted';
  const showSubscribe = !IS_NATIVE && !active && !isGranted;
  const showManage = !IS_NATIVE && hasStripeCustomer && (active || isPastDue || cancelAtPeriodEnd);

  async function subscribe(planPick) {
    setBusy(true);
    try {
      const { data } = await api.post('/me/sms/subscribe', { plan: planPick });
      // Redirect to Stripe Checkout.
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not start subscription');
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    try {
      const { data } = await api.post('/me/sms/portal');
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not open billing portal');
      setBusy(false);
    }
  }

  // Border + icon color reflect the strongest signal
  const borderColor = paymentFailed
    ? 'var(--fire)'
    : (active ? 'var(--goal)' : 'var(--border)');
  const Icon = paymentFailed
    ? AlertCircle
    : (active ? CheckCircle2 : AlertCircle);
  const iconColor = paymentFailed
    ? 'var(--fire)'
    : (active ? 'var(--goal)' : 'var(--text-3)');

  return (
    <div style={{
      padding: 12,
      background: 'var(--surface-2)',
      border: `1px solid ${borderColor}`,
      borderRadius: 8,
      fontSize: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <Icon size={14} color={iconColor} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Main status line */}
          {active ? (
            <strong>
              Messaging plan
              {isGranted && ' (comped by Game Day)'}
              {inTrial && ' — free trial'}
              {isPaidActive && plan === 'monthly' && ' — monthly'}
              {isPaidActive && plan === 'annual'  && ' — annual'}
              {isPastDue && ' — payment due'}
            </strong>
          ) : (
            <strong>No active Messaging subscription.</strong>
          )}

          {/* Sub-line: dates + actions */}
          {inTrial && trialEnd && (
            <div className="text-secondary" style={{ marginTop: 4 }}>
              Trial ends {new Date(trialEnd).toLocaleDateString()}. After that you'll be charged automatically unless you cancel.
            </div>
          )}
          {isPaidActive && periodEnd && !cancelAtPeriodEnd && (
            <div className="text-secondary" style={{ marginTop: 4 }}>
              Renews {new Date(periodEnd).toLocaleDateString()}.
            </div>
          )}
          {cancelAtPeriodEnd && periodEnd && (
            <div className="text-secondary" style={{ marginTop: 4 }}>
              Cancels {new Date(periodEnd).toLocaleDateString()}. SMS will keep working until then.
            </div>
          )}
          {isPastDue && (
            <div style={{ marginTop: 4, color: 'var(--fire)' }}>
              {IS_NATIVE
                ? 'Your last payment failed. SMS stops if it isn\'t sorted out within a few days.'
                : 'Your last payment failed. Update your card to keep receiving SMS — your subscription will be canceled automatically if payment isn\'t sorted out within a few days.'}
            </div>
          )}
          {!IS_NATIVE && !active && !isGranted && (
            <div className="text-secondary" style={{ marginTop: 4 }}>
              Subscribe to receive SMS for game reminders and announcements.
            </div>
          )}

          {/* Buttons */}
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {showSubscribe && (
              <>
                <button type="button" onClick={() => subscribe('monthly')} disabled={busy}
                        style={subscribeBtnPrimary}>
                  {busy ? '…' : 'Subscribe monthly'}
                </button>
                <button type="button" onClick={() => subscribe('annual')} disabled={busy}
                        style={subscribeBtnGhost}>
                  Subscribe annual (save)
                </button>
              </>
            )}
            {showManage && (
              <button type="button" onClick={openPortal} disabled={busy}
                      style={subscribeBtnGhost}>
                {busy ? '…' : 'Manage subscription'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const subscribeBtnPrimary = {
  padding: '6px 14px',
  background: 'var(--ice)',
  color: '#000',
  border: 'none',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: 12,
  cursor: 'pointer',
};
const subscribeBtnGhost = {
  padding: '6px 14px',
  background: 'transparent',
  color: 'var(--text-2)',
  border: `1px solid var(--border)`,
  borderRadius: 6,
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
};

function PhoneRow({ phone, onChange }) {
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');

  async function makePrimary() {
    setBusy(true);
    try {
      await api.post(`/me/sms/phones/${phone.id}/primary`);
      toast.success('Primary updated');
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function optIn() {
    setBusy(true);
    try {
      await api.post(`/me/sms/phones/${phone.id}/opt-in`);
      toast.success('You opted in. Welcome aboard.');
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function optOut() {
    if (!window.confirm('Stop receiving SMS notifications? Team announcements will still reach you via email.')) return;
    setBusy(true);
    try {
      await api.post(`/me/sms/phones/${phone.id}/opt-out`);
      toast.success('Opted out');
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function removePhone() {
    if (!window.confirm('Remove this phone number? You can re-add it later if you change your mind.')) return;
    setBusy(true);
    try {
      await api.delete(`/me/sms/phones/${phone.id}`);
      toast.success('Removed');
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!/^\d{6}$/.test(code)) {
      toast.error('Code must be 6 digits');
      return;
    }
    setVerifying(true);
    try {
      await api.post(`/me/sms/phones/${phone.id}/verify`, { code });
      toast.success('Verified! 🎉');
      setCode('');
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  }

  async function resend() {
    setVerifying(true);
    try {
      const { data } = await api.post(`/me/sms/phones/${phone.id}/resend`);
      if (data.send_status === 'sent') {
        toast.success('New code sent');
      } else {
        toast.error(`Code generated but SMS failed: ${data.send_error || 'unknown error'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to resend');
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div style={{
      padding: 12,
      background: 'var(--surface-2)',
      border: `1px solid var(--border)`,
      borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Phone size={14} color="var(--ice)" />
        <span style={{ fontWeight: 700, fontSize: 14, fontFamily: 'var(--font-mono)' }}>
          {phone.phone_e164}
        </span>
        <span className="text-secondary text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {phone.label}
        </span>
        {phone.is_sms_primary && (
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
            background: 'rgba(255,200,0,0.18)', color: '#d4a017',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <Star size={10} /> Primary
          </span>
        )}
        {phone.verified ? (
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
            background: 'rgba(20,184,77,0.18)', color: 'var(--goal)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Verified
          </span>
        ) : (
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
            background: 'rgba(244,67,54,0.15)', color: 'var(--fire)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Pending verification
          </span>
        )}
        {phone.consented && phone.verified && (
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
            background: 'rgba(0,168,255,0.18)', color: 'var(--ice)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Opted in
          </span>
        )}
        {phone.opted_out && (
          <span style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
            background: 'rgba(127,127,127,0.18)', color: 'var(--text-3)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>
            Opted out
          </span>
        )}
      </div>

      {/* Verification UI for unverified numbers */}
      {!phone.verified && (
        <div style={{ marginTop: 10 }}>
          <p className="text-secondary text-xs" style={{ marginBottom: 6 }}>
            Enter the 6-digit code we texted you.
          </p>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="000000"
              style={{
                padding: '7px 10px',
                background: 'var(--surface)',
                border: `1px solid var(--border)`,
                borderRadius: 6,
                fontSize: 14, fontFamily: 'var(--font-mono)',
                letterSpacing: '0.2em',
                width: 110,
                outline: 'none',
                color: 'var(--text)',
              }}
            />
            <button type="button" onClick={verify} disabled={verifying || code.length !== 6}
                    style={{ ...btnPrimary, opacity: code.length !== 6 ? 0.5 : 1 }}>
              {verifying ? '…' : 'Verify'}
            </button>
            <button type="button" onClick={resend} disabled={verifying}
                    style={btnGhost} title="Send a new code">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Action row for verified numbers */}
      {phone.verified && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {!phone.is_sms_primary && (
            <button type="button" onClick={makePrimary} disabled={busy} style={btnGhost}>
              Make primary
            </button>
          )}
          {!phone.consented ? (
            <button type="button" onClick={optIn} disabled={busy} style={btnPrimary}>
              Opt in to SMS
            </button>
          ) : !phone.opted_out ? (
            <button type="button" onClick={optOut} disabled={busy} style={btnGhost}>
              Opt out
            </button>
          ) : (
            <button type="button" onClick={optIn} disabled={busy} style={btnGhost}>
              Opt back in
            </button>
          )}
          <button type="button" onClick={removePhone} disabled={busy}
                  style={{ ...btnGhost, color: 'var(--fire)', borderColor: 'rgba(244,67,54,0.4)' }}>
            <Trash2 size={12} />
          </button>
        </div>
      )}

      {/* Consent fine print — required for TCPA opt-in record */}
      {phone.verified && !phone.consented && !phone.opted_out && (
        <p className="text-secondary" style={{ fontSize: 11, marginTop: 8 }}>
          By opting in, you consent to receive transactional SMS from Game Day about your
          team's games, schedule changes, and announcements. Message and data rates may apply.
          Reply STOP to unsubscribe.
        </p>
      )}
    </div>
  );
}

function AddPhoneForm({ onAdded, hasAny }) {
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('mobile');
  const [busy, setBusy] = useState(false);

  // Convert "(519) 555-1234" or "519-555-1234" or "5195551234" to
  // E.164 +15195551234 for the API. The backend enforces a strict
  // format; we do the friendly cleanup here.
  function normalizeToE164(input) {
    const digits = String(input || '').replace(/[^\d]/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (input.startsWith('+1') && /^\+1\d{10}$/.test(input)) return input;
    return null;
  }

  async function submit() {
    const e164 = normalizeToE164(phone);
    if (!e164) {
      toast.error('Use a 10-digit US/Canada number, e.g. 519-555-1234');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/me/sms/phones', {
        phone_e164: e164, label,
      });
      setPhone('');
      if (data.send_status === 'sent') {
        toast.success('Code sent — check your texts');
      } else {
        toast.error(`Number added but SMS failed: ${data.send_error || 'check admin SMS settings'}`);
      }
      await onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to add number');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      padding: 12,
      background: 'var(--surface-2)',
      border: `1px dashed var(--border)`,
      borderRadius: 10,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
        {hasAny ? 'Add another number' : 'Add a phone number'}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 200px' }}>
          <label className="text-secondary" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Phone number
          </label>
          <input type="tel"
                 value={phone}
                 onChange={(e) => setPhone(e.target.value)}
                 placeholder="519-555-1234"
                 style={{
                   width: '100%', marginTop: 3,
                   padding: '7px 10px',
                   background: 'var(--surface)',
                   border: `1px solid var(--border)`,
                   borderRadius: 6,
                   fontSize: 14, fontFamily: 'var(--font-mono)',
                   outline: 'none',
                   color: 'var(--text)',
                 }} />
        </div>
        <div>
          <label className="text-secondary" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Label
          </label>
          <select value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  style={{
                    marginTop: 3,
                    padding: '7px 10px',
                    background: 'var(--surface)',
                    border: `1px solid var(--border)`,
                    borderRadius: 6,
                    fontSize: 13,
                    outline: 'none',
                    color: 'var(--text)',
                  }}>
            <option value="mobile">Mobile</option>
            <option value="home">Home</option>
            <option value="work">Work</option>
            <option value="other">Other</option>
          </select>
        </div>
        <button type="button" onClick={submit} disabled={busy || !phone}
                style={{ ...btnPrimary, opacity: !phone ? 0.5 : 1 }}>
          {busy ? 'Sending…' : 'Add & send code'}
        </button>
      </div>
      <p className="text-secondary" style={{ fontSize: 11, marginTop: 8 }}>
        US and Canada only in this release. We'll text a 6-digit verification code.
      </p>
    </div>
  );
}

const btnPrimary = {
  padding: '7px 14px',
  background: 'var(--ice)',
  color: '#000',
  border: 'none',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: 12,
  cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px',
  background: 'transparent',
  color: 'var(--text-2)',
  border: `1px solid var(--border)`,
  borderRadius: 6,
  fontWeight: 600,
  fontSize: 12,
  cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};
